import { getAuthPort } from './auth';
import { config } from './config';
import { trackRequest } from '@/lib/request-progress';

/**
 * Cliente HTTP do Compilações.
 *
 * A única coisa que toda chamada precisa é o `Authorization: Bearer <token>`
 * obtido pela `AuthPort` (leitura: qualquer usuário autenticado; escrita:
 * capability conferida pela API). Toda requisição passa pelo contador de
 * progresso ambiente e todo erro vira `CompilacoesHttpError`, com mensagem que
 * cita o que falhou — a tela de erro nunca mostra "algo deu errado" genérico
 * (Playbook § Os quatro estados).
 */

export class CompilacoesHttpError extends Error {
  readonly status: number;
  readonly detalhe: string;

  constructor(recurso: string, status: number, detalhe: string) {
    super(
      status === 0
        ? `Não foi possível falar com a API do Compilações ao carregar ${recurso}.`
        : `A API do Compilações respondeu ${status} ao carregar ${recurso}.`,
    );
    this.name = 'CompilacoesHttpError';
    this.status = status;
    this.detalhe = detalhe;
  }
}

/**
 * Extrai a mensagem legível que a API manda no corpo do erro — `{"detail": ...}`
 * (HTTPException/validação do FastAPI) ou `{"message": ...}` (exceções da
 * `nst_fastapi_core`). O contrato garante que ela é sempre legível para o
 * usuário final; é ela que aparece nos 409/422 das ações de escrita.
 *
 * O `detail` de validação do FastAPI (422) pode vir como lista de erros por
 * campo — nesse caso as mensagens (`msg`) são juntadas.
 */
export function mensagemDoErro(erro: unknown): string | null {
  if (!(erro instanceof CompilacoesHttpError)) return null;
  try {
    const corpo = JSON.parse(erro.detalhe) as { message?: unknown; detail?: unknown };
    const mensagem = corpo.message ?? corpo.detail;
    if (typeof mensagem === 'string' && mensagem.trim() !== '') return mensagem;
    if (Array.isArray(mensagem)) {
      const textos = mensagem
        .map((item) => (item && typeof item === 'object' ? (item as { msg?: unknown }).msg : null))
        .filter((item): item is string => typeof item === 'string' && item.trim() !== '');
      return textos.length > 0 ? textos.join(' ') : null;
    }
    return null;
  } catch {
    return null;
  }
}

async function headers(): Promise<Record<string, string>> {
  const token = await getAuthPort().getToken();
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

type ValorQuery = string | number | boolean | null | undefined | Array<string | number>;

export function buildUrl(path: string, query?: Record<string, ValorQuery>): string {
  // A base pode ser relativa (o proxy de desenvolvimento, `/compilacoes-api`);
  // sem a origem explícita o construtor de URL rejeita caminho relativo.
  const url = new URL(path.startsWith('http') ? path : `${config.apiBaseUrl}${path}`, window.location.origin);
  Object.entries(query ?? {}).forEach(([chave, valor]) => {
    if (!chave || valor == null || valor === '') return;
    // `status` pode repetir (`?status=nao_homologado&status=reprovado`).
    if (Array.isArray(valor)) valor.forEach((item) => url.searchParams.append(chave, String(item)));
    else url.searchParams.set(chave, String(valor));
  });
  return url.toString();
}

interface RequestOptions {
  /** Nome legível do recurso, usado na mensagem de erro exibida ao usuário. */
  recurso: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, ValorQuery>;
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const { recurso, method = 'GET', query, body, signal } = options;

  return trackRequest(async () => {
    let response: Response;
    try {
      response = await fetch(buildUrl(path, query), {
        method,
        headers: await headers(),
        signal,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (causa) {
      if (causa instanceof DOMException && causa.name === 'AbortError') throw causa;
      throw new CompilacoesHttpError(recurso, 0, causa instanceof Error ? causa.message : String(causa));
    }

    if (!response.ok) {
      throw new CompilacoesHttpError(recurso, response.status, await response.text().catch(() => ''));
    }

    if (response.status === 204) return undefined as T;

    return (await response.json().catch(() => undefined)) as T;
  });
}

/** 403 do servidor: a área não está disponível para o perfil (não é falha, é "sem acesso"). */
export function ehAcessoNegado(erro: unknown): boolean {
  return erro instanceof CompilacoesHttpError && erro.status === 403;
}
